import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Star, Flame, Wheat, Leaf, Clock, MapPin, Phone } from "lucide-react";
import { IMAGES, CONTACTS, SIGNATURE_PIZZAS, REVIEWS } from "../data/site";
import { INGREDIENTS_EN } from "../data/menu_en";
import { Reveal, LineMask, SectionLabel } from "../components/Reveal";
import { useLang } from "../i18n";

const WHY_ICONS = [Wheat, Clock, Flame, Leaf];

const REVIEWS_EN = {
  "Fabio G.": "Pizzeria Bella has reopened and it was a real trip down memory lane. The same delicious dough, the same pizza that makes you say “this is the one!”. It's not just a pizzeria, it's a piece of our Acireale history.",
  "Gianluigi C.": "Amazing pizzas: crumbly, perfectly crisp, light and easy to digest. Impeccable, well-organised service, top kindness. Great value for money.",
  "Giuseppe M.": "Everything was wonderful, from the welcome to the service. The pizza chefs are brilliant, everything was so good. I will definitely be back. Well done!",
  "Antonino P.": "Absolutely one of the best pizzerias in the province of Catania!",
  "Giulia P.": "Absolutely worth trying! A certainty.",
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const { t, lang } = useLang();

  const [revStart, setRevStart] = useState(0);
  const [sigIndex, setSigIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setRevStart((s) => (s + 1) % REVIEWS.length), 5000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setSigIndex((s) => (s + 1) % SIGNATURE_PIZZAS.length), 6000);
    return () => clearInterval(id);
  }, []);
  const sig = SIGNATURE_PIZZAS[sigIndex];
  const visibleReviews = [0, 1, 2].map((i) => REVIEWS[(revStart + i) % REVIEWS.length]);

  const INFO = [
    { icon: MapPin, title: t("info.where"), lines: [CONTACTS.address] },
    { icon: Clock, title: t("info.hours"), lines: [t("info.days"), t("info.closed")] },
    { icon: Phone, title: t("info.contacts"), lines: [CONTACTS.phone, t("info.wa")] },
  ];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="Pizza appena sfornata da Bella" style={{ objectPosition: "center" }}
            className="w-full h-full object-cover brightness-110 contrast-105 saturate-110" />
          <div className="absolute inset-0 bg-ink/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/40" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 pt-40 w-full text-center">
          <LineMask delay={0.15}>
            <span className="font-head text-neon text-sm sm:text-base font-semibold tracking-[0.35em] uppercase">
              {t("hero.kicker")}
            </span>
          </LineMask>
          <h1 className="leading-[0.9] mt-4" data-testid="hero-title">
            <LineMask delay={0.3}>
              <img src="/assets/bella-script.png" alt="bella"
                className="h-[clamp(6rem,17vw,13rem)] w-auto mx-auto" />
            </LineMask>
            <LineMask delay={0.45} className="mt-10 leading-[1.2] pb-1">
              <span className="font-display uppercase tracking-tight text-[clamp(2rem,6.5vw,5rem)] text-outline-lime">{t("hero.tag1")}</span>
            </LineMask>
            <LineMask delay={0.6} className="leading-[1.2] pb-2">
              <span className="font-display uppercase tracking-tight text-[clamp(2rem,6.5vw,5rem)] text-neon">{t("hero.tag2")}</span>
            </LineMask>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap justify-center gap-4"
          >
            <Link to="/prenota" data-testid="hero-cta-prenota"
              className="bg-neon text-ink font-head font-bold tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-transform duration-300 hover:scale-105 hover:-rotate-1 hard-shadow">
              {t("hero.book")}
            </Link>
            <Link to="/menu" data-testid="hero-cta-menu"
              className="group border border-foreground/40 text-foreground font-head font-bold tracking-[0.15em] uppercase px-8 py-4 rounded-full hover:border-neon hover:text-neon transition-colors duration-300 flex items-center gap-2">
              {t("hero.menu")}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionLabel number="01">{t("intro.label")}</SectionLabel>
            <h2 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-5xl leading-none text-foreground">
              {t("intro.l1")}<br />{t("intro.l2")}<br /><span className="text-neon">{t("intro.l3")}</span>
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
          <Reveal delay={0.15}>
            <p className="text-lg sm:text-xl text-fog leading-relaxed">{t("intro.text")}</p>
            <Link to="/chi-siamo" data-testid="intro-cta-chi-siamo"
              className="inline-flex items-center gap-2 mt-8 text-neon font-head font-semibold tracking-[0.2em] uppercase text-sm group">
              {t("intro.link")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SIGNATURE PIZZAS */}
      <section className="bg-pine border-y border-neon/10 py-28 sm:py-36" data-testid="signature-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionLabel number="02">{t("sig.label")}</SectionLabel>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
              <h2 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-6xl leading-none text-foreground">
                {t("sig.l1")}<br />{t("sig.l2")}
              </h2>
              <Link to="/menu" data-testid="signature-cta-menu"
                className="group flex items-center gap-2 text-neon font-head font-semibold tracking-[0.2em] uppercase text-sm">
                {t("sig.all")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>

          <Reveal>
            <div className="relative overflow-hidden rounded-sm border border-neon/15 group/slider" data-testid="signature-slider">
              <AnimatePresence mode="wait">
                <motion.div
                  key={sig.name}
                  initial={{ opacity: 0, x: 80 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -80 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="relative"
                >
                  <img src={sig.image} alt={`Pizza ${sig.name}`}
                    className="w-full aspect-[3/4] sm:aspect-[16/8] object-cover brightness-105 saturate-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                  <div className="absolute top-5 right-6 font-display text-sm tracking-[0.3em] text-foreground/80" data-testid="signature-counter">
                    {String(sigIndex + 1).padStart(2, "0")} / {String(SIGNATURE_PIZZAS.length).padStart(2, "0")}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
                    <div className="flex items-baseline gap-4 max-w-3xl">
                      <h3 className="font-display uppercase text-4xl sm:text-6xl text-foreground" data-testid="signature-name">{sig.name}</h3>
                      <span className="flex-1 border-b border-dotted border-foreground/30 -translate-y-2 hidden sm:block" />
                      <span className="font-display text-3xl sm:text-4xl text-neon" data-testid="signature-price">€{sig.price}</span>
                    </div>
                    <p className="text-fog mt-3 max-w-2xl leading-relaxed text-sm sm:text-base" data-testid="signature-ingredients">
                      {lang === "en" ? INGREDIENTS_EN[sig.name] ?? sig.ingredients : sig.ingredients}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>

              <button
                onClick={() => setSigIndex((sigIndex - 1 + SIGNATURE_PIZZAS.length) % SIGNATURE_PIZZAS.length)}
                aria-label="Pizza precedente" data-testid="signature-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ink/60 backdrop-blur border border-neon/30 flex items-center justify-center text-neon hover:bg-neon hover:text-ink transition-colors duration-300"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => setSigIndex((sigIndex + 1) % SIGNATURE_PIZZAS.length)}
                aria-label="Pizza successiva" data-testid="signature-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ink/60 backdrop-blur border border-neon/30 flex items-center justify-center text-neon hover:bg-neon hover:text-ink transition-colors duration-300"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="flex justify-center gap-2.5 mt-6" data-testid="signature-names">
              {SIGNATURE_PIZZAS.map((p, i) => (
                <button
                  key={p.name}
                  onClick={() => setSigIndex(i)}
                  aria-label={p.name}
                  data-testid={`signature-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${i === sigIndex ? "w-8 bg-neon" : "w-1.5 bg-foreground/25 hover:bg-foreground/50"}`}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* LA SPEZZATA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36" data-testid="spezzata-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 lg:sticky lg:top-32">
            <Reveal>
              <SectionLabel number="03">{t("sp.label")}</SectionLabel>
              <h2 className="font-display uppercase tracking-tight text-5xl sm:text-7xl leading-[0.95] text-foreground">
                {t("sp.title")}
              </h2>
              <p className="text-lg leading-relaxed mt-8 max-w-xl text-foreground">{t("sp.p2")}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden rounded-sm group">
                <img src={IMAGES.spezzata} alt="La Spezzettata di Bella" loading="lazy"
                  className="w-full aspect-[4/3] object-cover brightness-105 saturate-110 transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink/80 to-transparent p-6">
                  <span className="font-head text-neon text-xs font-semibold tracking-[0.3em] uppercase">Dal 1980 · Acireale</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="overflow-hidden rounded-sm group" data-testid="spezzata-photo-salata">
                  <img src={IMAGES.spezzataSalata} alt="Spezzettata con pomodoro fresco e rucola" loading="lazy"
                    className="w-full aspect-[4/3] object-cover brightness-105 saturate-110 transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="overflow-hidden rounded-sm group" data-testid="spezzata-photo-dolce">
                  <img src={IMAGES.spezzataDolce} alt="Spezzettata dolce con Nutella" loading="lazy"
                    className="w-full aspect-[4/3] object-cover brightness-105 saturate-110 transition-transform duration-700 group-hover:scale-105" />
                </div>
              </div>
              <p className="text-lg text-fog leading-relaxed mt-6">{t("sp.p1")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY BELLA */}
      <section className="bg-pine border-y border-neon/10 py-28 sm:py-36" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel number="04">{t("why.label")}</SectionLabel>
          <h2 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-6xl leading-none text-foreground mb-16">
            {t("why.l1")}<br /><span className="text-outline-lime">{t("why.l2")}</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-neon/15 border border-neon/15">
          {WHY_ICONS.map((Icon, i) => (
            <Reveal key={i} delay={i * 0.1} className="bg-ink">
              <div className="p-8 h-full group hover:bg-moss/50 transition-colors duration-500" data-testid={`why-card-${i}`}>
                <span className="font-display text-outline-faint text-5xl">0{i + 1}</span>
                <Icon size={28} className="text-neon mt-6 mb-4 transition-transform duration-500 group-hover:-rotate-12 group-hover:scale-110" />
                <h3 className="font-head font-bold uppercase tracking-wide text-xl text-foreground">{t(`why.${i}.t`)}</h3>
                <p className="text-sm text-fog mt-3 leading-relaxed">{t(`why.${i}.d`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-pine border-y border-neon/10 py-28 sm:py-36" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionLabel number="05">{t("rev.label")}</SectionLabel>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
              <h2 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-6xl leading-none text-foreground">
                {t("rev.l1")}<br />{t("rev.l2")}
              </h2>
              <div className="flex items-center gap-3 border border-neon/25 px-5 py-3" data-testid="google-rating-badge">
                <span className="font-display text-4xl text-neon">4.5</span>
                <div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} className={i < 4 ? "fill-neon text-neon" : "fill-neon/40 text-neon/40"} />
                    ))}
                  </div>
                  <span className="text-xs text-fog tracking-widest uppercase font-head">{t("rev.onGoogle")}</span>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="reviews-rotator">
            {visibleReviews.map((r, i) => (
              <div key={i} className="h-full">
                <AnimatePresence mode="wait">
                  <motion.figure
                    key={r.author}
                    initial={{ opacity: 0, y: 24, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -16, scale: 0.98, transition: { duration: 0.4, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }}
                    transition={{ duration: 0.7, delay: i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                    className="border border-neon/15 bg-ink p-8 h-full flex flex-col hover:border-neon/40 transition-colors duration-500 rounded-sm"
                    data-testid={`review-card-${i}`}
                  >
                    <div className="flex gap-1 mb-5">
                      {[...Array(r.stars)].map((_, s) => (
                        <Star key={s} size={15} className="fill-neon text-neon" />
                      ))}
                    </div>
                    <blockquote className="text-fog leading-relaxed flex-1">“{lang === "en" ? REVIEWS_EN[r.author] : r.text}”</blockquote>
                    <figcaption className="mt-6 font-head font-semibold uppercase tracking-[0.2em] text-sm text-foreground">
                      {r.author} <span className="text-fog/60 normal-case tracking-normal font-body font-normal">{t("rev.google")}</span>
                    </figcaption>
                  </motion.figure>
                </AnimatePresence>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-2.5 mt-8">
            {REVIEWS.map((r, i) => (
              <button
                key={r.author}
                onClick={() => setRevStart(i)}
                aria-label={`Recensione ${i + 1}`}
                data-testid={`review-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === revStart ? "w-8 bg-neon" : "w-1.5 bg-foreground/25 hover:bg-foreground/50"}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* LOCALE */}
      <section className="relative py-28 sm:py-36 overflow-hidden" data-testid="locale-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <SectionLabel number="06">{t("loc.label")}</SectionLabel>
              <h2 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-5xl leading-none text-foreground">
                {t("loc.l1")}<br />{t("loc.l2")}<br /><span className="text-neon">{t("loc.l3")}</span>
              </h2>
              <p className="text-fog leading-relaxed mt-6 max-w-md">{t("loc.text")}</p>
              <Link to="/gallery" data-testid="locale-cta-gallery"
                className="inline-flex items-center gap-2 mt-8 text-neon font-head font-semibold tracking-[0.2em] uppercase text-sm group">
                {t("loc.link")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal delay={0.1}>
              <div className="relative">
                <img src={IMAGES.chef} alt="Il pizzaiolo di Bella al lavoro" loading="lazy"
                  className="relative w-full aspect-[4/3] object-cover rounded-sm" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INFO STRIP */}
      <section className="border-t border-neon/15 bg-pine" data-testid="info-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
          {INFO.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.1}>
              <div className="flex gap-5" data-testid={`info-block-${i}`}>
                <b.icon size={24} className="text-neon shrink-0 mt-1" />
                <div>
                  <h3 className="font-head font-bold uppercase tracking-[0.2em] text-sm text-foreground mb-2">{b.title}</h3>
                  {b.lines.map((l) => (
                    <p key={l} className="text-fog">{l}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="border-t border-neon/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 text-center">
            <Reveal>
              <h2 className="font-display uppercase text-4xl sm:text-6xl text-foreground leading-none">
                {t("bottom.l1")} <span className="text-neon">{t("bottom.l2")}</span>
              </h2>
              <Link to="/prenota" data-testid="bottom-cta-prenota"
                className="inline-block mt-8 bg-neon text-ink font-head font-bold tracking-[0.15em] uppercase px-10 py-4 rounded-full transition-transform duration-300 hover:scale-105 hover:-rotate-1">
                {t("bottom.book")}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
